import {
  getCurrencyInputStep,
  isFutureDate,
  toSentenceCase,
} from '@omnipresentgroup/helpers';
import type {
  CompensationVariantPayment,
  CompensationVariantPaymentConfig,
} from '@omnipresentgroup/types';
import type { JSONSchema7 } from 'json-schema';
import { useEffect, useMemo, useState } from 'react';

import { type ContractualVariantPaymentFormFields, FormAction } from '../types';

export const useContractualVariantPaymentSchema = ({
  itemConfig,
  itemsConfig,
  currency,
  action,
  formValues,
  variantPayment,
}: {
  itemConfig?: CompensationVariantPaymentConfig;
  itemsConfig: CompensationVariantPaymentConfig[];
  currency: string;
  action: FormAction;
  formValues?: ContractualVariantPaymentFormFields;
  variantPayment?: CompensationVariantPayment;
}) => {
  const [schema, setSchema] = useState<JSONSchema7>({});
  const [uiSchema, setUiSchema] = useState({});

  const typeOptions = useMemo(() => {
    const types = itemsConfig.map((item) => item.type);
    const uniqueTypes = [...new Set(types)];
    return uniqueTypes
      .map((type) => ({ const: type, title: toSentenceCase(type) }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [itemsConfig]);

  const subtypeOptions = useMemo(() => {
    return itemsConfig
      .filter((item) => item.type === formValues?.type && item.subtype)
      .map((item) => ({
        const: item.subtype,
        title: item.name,
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [formValues?.type, itemsConfig]);

  const recurrenceOptions = useMemo(() => {
    return (
      itemsConfig
        .find(
          (item) =>
            item.type === formValues?.type &&
            item.subtype === formValues?.subtype,
        )
        ?.recurrence.map((rec) => ({
          const: rec,
          title: toSentenceCase(rec),
        }))
        .sort((a, b) => a.title.localeCompare(b.title)) || []
    );
  }, [formValues?.subtype, formValues?.type, itemsConfig]);

  useEffect(() => {
    setSchema({
      type: 'object',
      description: '',
      required: [
        ...(typeOptions.length > 0 ? ['type'] : []),
        ...(subtypeOptions.length > 0 ? ['subtype'] : []),
        ...(recurrenceOptions.length > 0 ? ['recurrence'] : []),
        'amount',
        'reason',
      ],
      properties: {
        type: {
          title: 'Type',
          type: 'string',
          ...(typeOptions?.length > 0 ? { oneOf: typeOptions } : {}),
          default: typeOptions?.length === 1 ? typeOptions[0].const : undefined,
        },
        subtype: {
          title: formValues?.type
            ? `${toSentenceCase(formValues.type)} type`
            : 'Subtype',
          type: 'string',
          ...(subtypeOptions?.length > 0 ? { oneOf: subtypeOptions } : {}),
        },
        recurrence: {
          title: 'Recurrence',
          type: 'string',
          ...(recurrenceOptions?.length > 0
            ? { oneOf: recurrenceOptions }
            : {}),
        },
        amount: {
          type: 'object',
          required: ['currency', 'amount'],
          properties: {
            currency: {
              title: 'Currency',
              type: 'string',
              default: currency,
              readOnly: true,
            },
            amount: {
              title: 'Gross amount',
              type: 'number',
              default: itemConfig?.rangeMin
                ? Number(itemConfig.rangeMin)
                : undefined,
              minimum: itemConfig?.rangeMin
                ? Number(itemConfig.rangeMin)
                : 0.01,
              maximum: itemConfig?.rangeMax
                ? Number(itemConfig.rangeMax)
                : undefined,
              readOnly:
                action === FormAction.UPDATE &&
                variantPayment &&
                !isFutureDate(variantPayment.effectiveDate),
            },
          },
        },
        reason: {
          title: 'Reason',
          type: 'string',
        },
        additionalNotes: {
          title: 'Additional notes',
          type: 'string',
        },
      },
    });

    setUiSchema({
      'ui:submitButtonOptions': {
        norender: true,
      },
      'ui:order': [
        'type',
        'subtype',
        'recurrence',
        'amount',
        'reason',
        'additionalNotes',
      ],
      type: {
        'ui:widget': typeOptions?.length > 0 ? 'radio' : 'hidden',
        'ui:readonly': action === FormAction.UPDATE,
      },
      subtype: {
        'ui:widget': subtypeOptions?.length > 0 ? undefined : 'hidden',
        'ui:readonly': action === FormAction.UPDATE,
      },
      recurrence: {
        'ui:widget': recurrenceOptions?.length > 0 ? 'radio' : 'hidden',
        'ui:readonly': action === FormAction.UPDATE,
      },
      amount: {
        'ui:columns': {
          columns: 6,
        },
        'ui:title': 'Gross amount',
        currency: {
          'ui:label': false,
          'ui:span': 2,
          'ui:options': {
            readonly: true,
          },
        },
        amount: {
          'ui:label': false,
          'ui:span': 4,
          'ui:options': {
            step: getCurrencyInputStep(currency),
          },
        },
      },
      reason: {
        'ui:widget': 'textarea',
        'ui:readonly': itemConfig?.mandatory,
        'ui:options': {
          rows: 1,
        },
      },
      additionalNotes: {
        'ui:widget': 'textarea',
        'ui:options': {
          rows: 1,
        },
      },
    });
  }, [
    action,
    currency,
    formValues?.type,
    itemConfig?.mandatory,
    itemConfig?.rangeMax,
    itemConfig?.rangeMin,
    itemConfig?.type,
    recurrenceOptions,
    subtypeOptions,
    typeOptions,
    variantPayment,
  ]);

  return {
    schema,
    uiSchema,
  };
};
